import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { useEditor, EditorContent } from "@tiptap/react";
import { getMarkRange } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { Markdown } from "tiptap-markdown";
import { OwcButton, OwcInput, OwcModalDialog } from "@one/react";

import { MAX_RICH_DESCRIPTION_LENGTH } from "./deriveShortDescription";
import "./RichDescriptionEditor.scss";

/**
 * Editor for the booking rich description (RETINA-7469053).
 *
 * - Markdown in/out (single source of truth = markdown string).
 * - Toolbar limited to B / I / U / link per AC-01 / AC-05 / AC-06.
 * - Underline serializes to inline `<u>...</u>` (BE whitelist).
 * - Paste from JIRA-like sources keeps links thanks to ProseMirror clipboard
 *   parsers (AC-09).
 * - Bare URLs (AC-08) are auto-linked while editing (Link extension with
 *   `autolink`); renderer side handles read-only display.
 * - Hard 1000 character raw-MD limit (BE rejects above that).
 */
function RichDescriptionEditor({
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  ariaLabel,
  label,
  errorText,
  maxLength,
  testId,
}) {
  const handleUpdate = useCallback(
    ({ editor }) => {
      if (!onChange) return;
      // tiptap-markdown exposes editor.storage.markdown.getMarkdown()
      const md = editor.storage.markdown.getMarkdown() || "";
      onChange(md);
    },
    [onChange]
  );

  // Tick forces a React re-render on every editor transaction (typing, click,
  // selection move) so the toolbar `is-active` state stays in sync with the
  // current cursor / selection. Without this, the toolbar buttons would only
  // reflect whatever was active at the moment of the last React render.
  const [, setTick] = useState(0);
  const bumpTick = useCallback(() => setTick((n) => (n + 1) % 1_000_000), []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // MVP: only bold + italic from StarterKit; disable rest per AC scope.
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
        strike: false,
        hardBreak: { keepMarks: false },
      }),
      Underline,
      Link.extend({
        // Append a native browser tooltip showing the underlying URL on hover
        // so users can verify where a link points without clicking it.
        renderHTML({ HTMLAttributes }) {
          return ["a", { ...HTMLAttributes, title: HTMLAttributes.href }, 0];
        },
      }).configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        protocols: ["http", "https", "mailto"],
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Markdown.configure({
        html: true, // allow <u> round-trip
        tightLists: true,
        linkify: false,
        breaks: false,
        transformPastedText: false,
        transformCopiedText: false,
      }),
    ],
    content: value || "",
    editable: !disabled,
    onUpdate: handleUpdate,
    onBlur: () => onBlur?.(),
    onSelectionUpdate: bumpTick,
    onTransaction: bumpTick,
    editorProps: {
      attributes: {
        "aria-label": ariaLabel || "Rich description",
        "data-testid": testId ? `${testId}-content` : "rich-description-content",
        role: "textbox",
        "aria-multiline": "true",
      },
      handleKeyDown: (view, event) => {
        if (event.key !== " " || event.isComposing) {
          return false;
        }

        const { state } = view;
        const { from, to, empty } = state.selection;
        if (!empty) {
          return false;
        }

        const linkMarkType = state.schema.marks.link;
        if (!linkMarkType) {
          return false;
        }

        const $pos = state.doc.resolve(from);
        const markRange = getMarkRange($pos, linkMarkType);

        if (!markRange || from !== markRange.to) {
          return false;
        }

        event.preventDefault();
        const tr = state.tr;
        tr.removeStoredMark(linkMarkType);
        tr.setStoredMarks([]);
        tr.insertText(" ", from, to);
        view.dispatch(tr);
        return true;
      },
    },
  });

  // Sync external value -> editor (e.g. formik reset)
  useEffect(() => {
    if (!editor) return;
    const current = editor.storage.markdown.getMarkdown() || "";
    const next = value || "";
    if (current !== next) {
      editor.commands.setContent(next, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  const currentLength = (value || "").length;
  const overLimit = currentLength > maxLength;
  const hasError = Boolean(errorText) || overLimit;

  // --- Link dialog ---------------------------------------------------------
  // Replaces window.prompt with an OWC modal matching the rest of the app.
  // We must:
  //  - snapshot the editor selection BEFORE the modal steals focus,
  //  - restore it on confirm,
  //  - and INSERT new text (with link mark) when nothing was selected, because
  //    `setLink` only paints a mark over an existing range — without text it
  //    is a no-op (the original bug: "click OK, nothing happens").
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [isEditingExistingLink, setIsEditingExistingLink] = useState(false);
  const savedSelectionRef = useRef(null);

  const closeLinkDialog = useCallback(() => {
    setLinkDialogOpen(false);
    setLinkUrl("");
    setLinkText("");
    setIsEditingExistingLink(false);
    savedSelectionRef.current = null;
  }, []);

  const handleLinkClick = useCallback(() => {
    if (!editor) return;
    const { from, to, empty } = editor.state.selection;
    const previousUrl = editor.getAttributes("link").href || "";

    // If the cursor sits inside an existing link (even with no selection),
    // expand both the saved selection AND the visible "Link text" field to
    // cover the entire link mark so the user sees what they're editing.
    let effectiveFrom = from;
    let effectiveTo = to;
    if (previousUrl) {
      const linkMarkType = editor.schema.marks.link;
      if (linkMarkType) {
        const $pos = editor.state.doc.resolve(from);
        const markRange = getMarkRange($pos, linkMarkType);
        if (markRange) {
          effectiveFrom = markRange.from;
          effectiveTo = markRange.to;
        }
      }
    }

    savedSelectionRef.current = { from: effectiveFrom, to: effectiveTo };
    const hasRange = effectiveFrom !== effectiveTo;
    const selectedText = hasRange
      ? editor.state.doc.textBetween(effectiveFrom, effectiveTo, " ")
      : empty
        ? ""
        : editor.state.doc.textBetween(from, to, " ");
    setIsEditingExistingLink(Boolean(previousUrl));
    setLinkUrl(previousUrl);
    setLinkText(selectedText);
    setLinkDialogOpen(true);
  }, [editor]);

  const normalizeUrl = (raw) => {
    const url = (raw || "").trim();
    if (!url) return "";
    if (/^(https?:\/\/|mailto:)/i.test(url)) return url;
    return `https://${url.replace(/^\/+/, "")}`;
  };

  const handleLinkConfirm = useCallback(() => {
    if (!editor) return;
    const normalized = normalizeUrl(linkUrl);
    const saved = savedSelectionRef.current;
    if (!normalized) {
      // empty url = remove link (only meaningful when editing an existing one)
      let chain = editor.chain().focus();
      if (saved) chain = chain.setTextSelection(saved);
      chain.extendMarkRange("link").unsetLink().run();
      closeLinkDialog();
      return;
    }

    const from = saved ? saved.from : editor.state.selection.from;
    const to = saved ? saved.to : editor.state.selection.to;
    const hadSelection = from !== to;
    const originalSelectedText = hadSelection
      ? editor.state.doc.textBetween(from, to, " ")
      : "";
    const desiredText = (linkText || "").trim();

    let chain = editor.chain().focus().setTextSelection({ from, to });

    if (!hadSelection) {
      // No selection: insert link text (user-supplied or fall back to URL).
      chain
        .insertContent({
          type: "text",
          text: desiredText || normalized,
          marks: [{ type: "link", attrs: { href: normalized } }],
        })
        .run();
    } else if (desiredText && desiredText !== originalSelectedText) {
      // User changed the visible label - replace selection with new text+link.
      chain
        .insertContent({
          type: "text",
          text: desiredText,
          marks: [{ type: "link", attrs: { href: normalized } }],
        })
        .run();
    } else {
      // Selection kept as-is: just (re)apply the link mark over the range.
      chain.extendMarkRange("link").setLink({ href: normalized }).run();
    }

    closeLinkDialog();
  }, [editor, linkUrl, linkText, closeLinkDialog]);

  const handleLinkRemove = useCallback(() => {
    if (!editor) return;
    const saved = savedSelectionRef.current;
    let chain = editor.chain().focus();
    if (saved) chain = chain.setTextSelection(saved);
    chain.extendMarkRange("link").unsetLink().run();
    closeLinkDialog();
  }, [editor, closeLinkDialog]);

  // NOTE: do NOT wrap this in useMemo. The toolbar's `is-active` state depends
  // on `editor.isActive(...)` which is a *getter* against the current editor
  // state. React re-renders are triggered by `bumpTick` on every selection /
  // transaction, but the editor reference (and other deps) stay stable - so a
  // useMemo here would always return the cached JSX captured at first render,
  // leaving the buttons stuck on whatever formatting was active where the
  // cursor first landed. Inline computation costs ~nothing.
  const renderToolbar = () => {
    if (!editor) return null;
    const btn = (key, label, isActive, onClick, title) => (
      <button
        key={key}
        type="button"
        className={`rich-description-editor__toolbar-button${isActive ? " is-active" : ""}`}
        onClick={onClick}
        disabled={disabled}
        aria-pressed={isActive}
        title={title}
        data-testid={testId ? `${testId}-btn-${key}` : `rich-description-btn-${key}`}
      >
        {label}
      </button>
    );
    return (
      <div className="rich-description-editor__toolbar" role="toolbar" aria-label="Formatting">
        {btn(
          "bold",
          <strong>B</strong>,
          editor.isActive("bold"),
          () => editor.chain().focus().toggleBold().run(),
          "Bold (Ctrl+B)"
        )}
        {btn(
          "italic",
          <em>I</em>,
          editor.isActive("italic"),
          () => editor.chain().focus().toggleItalic().run(),
          "Italic (Ctrl+I)"
        )}
        {btn(
          "underline",
          <u>U</u>,
          editor.isActive("underline"),
          () => editor.chain().focus().toggleUnderline().run(),
          "Underline (Ctrl+U)"
        )}
        <span className="rich-description-editor__toolbar-separator" />
        {btn("link", "🔗", editor.isActive("link"), handleLinkClick, "Add / edit link")}
      </div>
    );
  };

  return (
    <div
      className={`rich-description-editor${hasError ? " rich-description-editor--error" : ""}`}
      data-testid={testId || "rich-description-editor"}
    >
      {label && (
        <div className="rich-description-editor__label">{label}</div>
      )}
      {renderToolbar()}
      <div className="rich-description-editor__content">
        {editor ? (
          <EditorContent editor={editor} />
        ) : (
          <span aria-hidden="true">{placeholder}</span>
        )}
      </div>
      <div className="rich-description-editor__footer">
        <span aria-live="polite">
          <span
            className={`rich-description-editor__counter${
              overLimit ? " rich-description-editor__counter--over" : ""
            }`}
            data-testid={testId ? `${testId}-counter` : "rich-description-counter"}
          >
            {currentLength} / {maxLength}
          </span>
        </span>
      </div>
      {errorText && !overLimit && (
        <div className="rich-description-editor__error-text" role="alert">
          {errorText}
        </div>
      )}
      {overLimit && (
        <div className="rich-description-editor__error-text" role="alert">
          {`Description exceeds the ${maxLength} character limit.`}
        </div>
      )}
      {linkDialogOpen &&
        ReactDOM.createPortal(
          <OwcModalDialog
            visible={linkDialogOpen}
            size="sm"
            className="owcmodalZIndex"
            data-testid={testId ? `${testId}-link-dialog` : "rich-description-link-dialog"}
            onVisibleChange={(e) => {
              if (!e.target.visible) closeLinkDialog();
            }}
          >
            <div slot="header">
              {isEditingExistingLink ? "Edit link" : "Insert link"}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                minWidth: "320px",
                padding: "4px 0 16px",
              }}
            >
              <OwcInput
                label="Link text"
                placeholder="Visible text (optional)"
                value={linkText}
                data-testid={
                  testId ? `${testId}-link-dialog-text` : "rich-description-link-dialog-text"
                }
                onInputChange={(event) => setLinkText(event.target?.value || "")}
              />
              <OwcInput
                label="URL"
                placeholder="https://example.com"
                value={linkUrl}
                data-testid={
                  testId ? `${testId}-link-dialog-url` : "rich-description-link-dialog-url"
                }
                onInputChange={(event) => setLinkUrl(event.target?.value || "")}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleLinkConfirm();
                  }
                }}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginTop: "4px",
                }}
              >
                {isEditingExistingLink && (
                  <OwcButton
                    variant="secondary"
                    flat
                    data-testid={
                      testId
                        ? `${testId}-link-dialog-remove`
                        : "rich-description-link-dialog-remove"
                    }
                    onClick={handleLinkRemove}
                  >
                    Remove link
                  </OwcButton>
                )}
                <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
                  <OwcButton
                    variant="secondary"
                    flat
                    data-testid={
                      testId
                        ? `${testId}-link-dialog-cancel`
                        : "rich-description-link-dialog-cancel"
                    }
                    onClick={closeLinkDialog}
                  >
                    Cancel
                  </OwcButton>
                  <OwcButton
                    variant="primary"
                    flat
                    disabled={!linkUrl.trim()}
                    data-testid={
                      testId ? `${testId}-link-dialog-ok` : "rich-description-link-dialog-ok"
                    }
                    onClick={handleLinkConfirm}
                  >
                    {isEditingExistingLink ? "Update" : "Insert"}
                  </OwcButton>
                </div>
              </div>
            </div>
          </OwcModalDialog>,
          document.body
        )}
    </div>
  );
}

RichDescriptionEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  label: PropTypes.string,
  errorText: PropTypes.string,
  maxLength: PropTypes.number,
  testId: PropTypes.string,
};

RichDescriptionEditor.defaultProps = {
  value: "",
  onChange: undefined,
  onBlur: undefined,
  disabled: false,
  placeholder: "",
  ariaLabel: undefined,
  label: undefined,
  errorText: undefined,
  maxLength: MAX_RICH_DESCRIPTION_LENGTH,
  testId: undefined,
};

export default RichDescriptionEditor;
